import { useEffect, useState, useMemo } from 'react';
import { contentAPI, dynamicAPI } from '../services/api';
import { useSiteSettings } from '../hooks/useContent';
import { usePageSeo } from '../hooks/usePageSeo';
import { buildOrganizationSchema, buildBreadcrumbSchema } from '../utils/seo';
import EventCard from '../components/EventCard';
import LoadingIndicator from '../components/LoadingIndicator';
import './Blogs.css';
import './Expeditions.css';


const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400';

function hasUpcomingDeparture(trek) {
  const today = new Date();
  const todayStamp = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const candidates = Array.isArray(trek?.departureDates) ? trek.departureDates : [];
  return candidates.some((dateValue) => {
    if (!dateValue) return false;
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return false;
    const stamp = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()).getTime();
    return stamp >= todayStamp;
  });
}

function trekToCard(trek) {
  const j = trek?.data?.data ?? trek;
  if (!j?.id) return null;
  const media = j.media ?? j.mediaList ?? [];
  const imageUrl = media[0]?.url || j.imageUrl || FALLBACK_IMAGE;
  const pkg = (j.packages ?? [])[0];
  let durationDays = null;
  if (pkg) {
    if (pkg.durationDays != null && Number(pkg.durationDays) > 0) {
      durationDays = Number(pkg.durationDays);
    } else if (pkg.endDayOffset != null && pkg.startDayOffset != null) {
      durationDays = Math.max(1, Number(pkg.endDayOffset) - Number(pkg.startDayOffset) + 1);
    } else if (pkg.endDay != null && pkg.startDay != null) {
      durationDays = Math.max(1, Number(pkg.endDay) - Number(pkg.startDay) + 1);
    }
  }
  // Final fallback: use itinerary length if available
  if (durationDays == null && Array.isArray(j.scheduleDays) && j.scheduleDays.length > 0) {
    durationDays = j.scheduleDays.length;
  }
  const duration = durationDays != null ? `${durationDays} Day${durationDays !== 1 ? 's' : ''}` : 'TBA';
  const todayStr = new Date().toISOString().slice(0, 10);
  const departureDates = (Array.isArray(j.batches) ? j.batches : [])
    .filter((b) => {
      if (!b?.startDate) return false;
      if (b.isActive === false) return false;
      const st = String(b.status || '').toLowerCase();
      if (['cancelled', 'inactive', 'completed'].includes(st)) return false;
      if (String(b.startDate) < todayStr) return false;
      if (b.endDate && String(b.endDate) < todayStr) return false;
      return true;
    })
    .map((b) => b.startDate)
    .sort((a, b) => new Date(a) - new Date(b));
  if (!departureDates.length) return null;
  return {
    id: j.id,
    name: j.name || j.title,
    slug: j.slug,
    imageUrl,
    duration,
    difficulty: j.difficulty || 'Moderate',
    tagline: j.tagline,
    description: j.description,
    price: j.packages?.[0]?.price ?? null,
    location: j.location ?? null,
    departureDates,
    media: Array.isArray(media) ? media.map((m) => ({ id: m.id, url: m.url })) : []
  };
}

export default function Expeditions() {
  const { settings } = useSiteSettings();
  const [mainTitle, setMainTitle] = useState('Special Events');
  const [heroImage, setHeroImage] = useState('');
  const [heroSubtitle, setHeroSubtitle] = useState('');
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  const publicBase =
    String(settings.site_public_url || '').replace(/\/$/, '') ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const brand = settings.site_brand_name || 'Invincible NGO';

  const displayHeroSubtitle =
    (heroSubtitle || settings.special_events_subtitle || 'Unique Experiences Curated For The Bold').trim();
  const heroClassName = heroImage
    ? 'blog-hero-banner blog-hero-banner-with-image'
    : 'blog-hero-banner';

  const expeditionsSchema = useMemo(() => {
    const org = buildOrganizationSchema(settings);
    const breadcrumbs = buildBreadcrumbSchema([
      { name: 'Expeditions & Special Events', url: '/expeditions' }
    ], publicBase);
    return {
      '@context': 'https://schema.org',
      '@graph': [org, breadcrumbs]
    };
  }, [settings, publicBase]);

  usePageSeo({
    title: `${mainTitle || 'Expeditions & Special Events'} | ${brand}`,
    description: displayHeroSubtitle || 'Unique Himalayan expeditions and curated trekking experiences for adventure enthusiasts.',
    canonical: publicBase ? `${publicBase}/expeditions` : undefined,
    image: heroImage || undefined,
    schema: expeditionsSchema
  });


  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await contentAPI.getExpeditionSection();
        if (cancelled) return;
        const data = res?.data?.data;
        const rawSections = Array.isArray(data?.sections) ? data.sections : [];
        const incomingTitle = String(data?.mainTitle || '').trim();
        setMainTitle(incomingTitle && incomingTitle !== 'Expeditions' ? incomingTitle : 'Special Events');
        setHeroImage(String(data?.heroImage || '').trim());
        setHeroSubtitle(String(data?.heroSubtitle || '').trim());

        const sectionsWithTreks = await Promise.all(
          rawSections.map(async (sec) => {
            const trekIds = Array.isArray(sec.trekIds) ? sec.trekIds.filter(Boolean) : [];
            let treks = [];

            // 1) Prefer backend-precomputed cards (with correct duration)
            if (Array.isArray(sec.treks) && sec.treks.length > 0) {
              treks = sec.treks
                .map((t) => {
                  if (!t || !t.id) return null;
                  return {
                    id: t.id,
                    name: t.name || t.title,
                    slug: t.slug,
                    imageUrl: t.imageUrl || FALLBACK_IMAGE,
                    duration: t.duration || 'TBA',
                    difficulty: t.difficulty || 'Moderate',
                    tagline: t.tagline,
                    description: t.description,
                    price: t.price ?? null,
                    location: t.location ?? null,
                    departureDates: Array.isArray(t.departureDates) ? t.departureDates : [],
                    media: Array.isArray(t.media) ? t.media : []
                  };
                })
                .filter(hasUpcomingDeparture);
            }

            // 2) Fallback: fetch full trek data and derive card locally
            if (!treks.length && trekIds.length > 0) {
              const results = await Promise.allSettled(
                trekIds.map((id) => dynamicAPI.getTrekById(id))
              );
              treks = results
                .map((r) =>
                  r.status === 'fulfilled'
                    ? trekToCard(r.value?.data?.data ?? r.value?.data)
                    : null
                )
                .filter(Boolean)
                .filter(hasUpcomingDeparture);
            }

            return { title: sec.title || 'Section', trekIds, treks };
          })
        );
        if (!cancelled) setSections(sectionsWithTreks);
      } catch {
        if (!cancelled) {
          setMainTitle('Special Events');
          setHeroImage('');
          setHeroSubtitle('');
          setSections([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const visibleSections = useMemo(
    () => sections.filter((sec) => Array.isArray(sec.treks) && sec.treks.length > 0),
    [sections]
  );

  return (
    <div className="expeditions-page">
      <div
        className={heroClassName}
        style={
          heroImage
            ? {
                backgroundImage: `linear-gradient(135deg, rgba(15,23,42,0.9) 0%, rgba(15,23,42,0.75) 40%, rgba(15,23,42,0.55) 100%), url(${heroImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }
            : undefined
        }
      >
        <div className="blog-hero-inner">
          <h1 className="blog-hero-title">{mainTitle}</h1>
          {displayHeroSubtitle ? (
            <p className="blog-hero-subtitle">{displayHeroSubtitle}</p>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div className="expeditions-loading">
          <LoadingIndicator label="Loading..." />
        </div>
      ) : (
        <section className="expeditions-section">
          <div className="expeditions-section-inner">
            {visibleSections.length === 0 ? (
              <div className="expeditions-empty">No special events are currently live. Check back soon!</div>
            ) : (
              visibleSections.map((sec, idx) => (
                <div key={sec.title || idx} className="expeditions-subsection">
                  <h2 className="expeditions-section-title">{sec.title || 'Section'}</h2>
                  <div className="expeditions-carousel">
                    {sec.treks.map((trek) => (
                      <EventCard
                        key={trek.id}
                        event={{
                          id: trek.id,
                          title: trek.name,
                          slug: trek.slug,
                          imageUrl: trek.imageUrl || FALLBACK_IMAGE,
                          duration: trek.duration || 'TBA',
                          location: trek.location || undefined,
                          difficulty: trek.difficulty || 'Moderate',
                          shortDescription: trek.tagline || trek.description || 'Experience the adventure.',
                          price: trek.price ?? null,
                          departureDates: Array.isArray(trek.departureDates) ? trek.departureDates : undefined,
                          media: Array.isArray(trek.media) ? trek.media : undefined
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      )}
    </div>
  );
}
